import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import { Box, Button, Card, Collapse, Typography } from "@mui/material";
import {
  MyHrUsersImportCollapseLabel,
  MyHrUsersImportDialog,
} from "my-hr/components";
import { MY_HR_GET_IMPORT_USERS_COUNT } from "my-hr/graphql";
import { OcFormSwitch, OcRoleFilter } from "oc/components";
import { useOcClientState, useOcProperty } from "oc/hooks";

export const MyHrUsersImportJDolBerCard = () => {
  const { t } = useTranslation();

  const [showDialog, setShowDialog] = useOcClientState<boolean>(
    "showJDolBerUsersImportDialog",
    false,
  );

  const initialRoleFilters: {
    company: any[];
    orgUnit: any[];
    workingPlace: any[];
    jobClassGroup: any[];
    payOffice: any[];
    contract: any[];
  } = {
    company: [],
    orgUnit: [],
    workingPlace: [],
    jobClassGroup: [],
    payOffice: [],
    contract: [],
  };

  const [roleFilters, setRoleFilters] = useState(initialRoleFilters);

  const handleAddFilter = ({
    key,
    value,
  }: {
    key: keyof typeof initialRoleFilters;
    value: any;
  }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].concat([value]);
    setRoleFilters(newState);
  };

  const handleRemoveFilter = ({
    key,
    value,
  }: {
    key: keyof typeof initialRoleFilters;
    value: any;
  }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].filter((item) => item !== value);
    setRoleFilters(newState);
  };

  const handleClearFilters = () => {
    setRoleFilters(initialRoleFilters);
  };

  const { data } = useQuery(MY_HR_GET_IMPORT_USERS_COUNT, {
    fetchPolicy: "cache-and-network",
    variables: { roleFilters },
  });

  const { data: importLockedData } = useOcProperty({
    resource: "system",
    key: "importLocked",
  });

  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  let importLocked = importLockedData === "false" ? false : true;

  const [showImportUsersCollapse, setShowImportUsersCollapse] =
    useOcClientState<boolean>("showImportJDolBerUsersRoleCollapse", false);

  const handleShowDialog = () => {
    setShowDialog(!showDialog);
  };

  const handleExpandClick = () => {
    setShowImportUsersCollapse(!showImportUsersCollapse);
  };

  const handleShowImportDialog = () => {
    handleShowDialog();
  };

  const handleCloseImportDialog = () => {
    setShowDialog(false);
  };

  const handleFinishImport = () => {
    setShowDialog(false);
  };

  const [automaticPasswordGeneration, setAutomaticPasswordGeneration] =
    React.useState(false);

  return (
    <Box>
      <MyHrUsersImportDialog
        onShowDialog={handleShowDialog}
        showDialog={showDialog}
        finishImport={handleFinishImport}
        importType="jdolber"
        onCloseDialog={handleCloseImportDialog}
        automaticPasswordGeneration={automaticPasswordGeneration}
        roleFilters={roleFilters}
      />
      <Card elevation={0}>
        <MyHrUsersImportCollapseLabel
          expanded={showImportUsersCollapse}
          handleExpandClick={handleExpandClick}
          label={t("MY_HR_IMPORT_USERS_FROM_JDOLBER")}
        />
        <Collapse in={showImportUsersCollapse} timeout="auto" unmountOnExit>
          <Box padding="0px 32px">
            <Box style={{ borderTop: "2px solid #C6CACC" }} />
            <Box height="24px" />
            <OcRoleFilter
              subtypeProps={{
                queryType: "import",
                product: "MyHR",
                role: "admin",
                roleFilters: roleFilters,
                handleAddItem: handleAddFilter,
                handleRemoveItem: handleRemoveFilter,
              }}
              summaryItems={[
                {
                  label: "OC_COUNT_OF_EMPLOYEES",
                  value: data?.importUsersCount || 0,
                  color: "primary",
                },
              ]}
              buttonBarProps={{
                primaryButtonText: "",
                rightButtonText: "",
                leftButtonText: t("OC_RESET"),
                leftButtonColor: "secondary",
                handleLeftButton: handleClearFilters,
                paddingTop: "10px",
                noLine: true,
              }}
            />
            {loginType === "legacy" && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  padding="36px 0px 0px 0px"
                  paddingBottom="12px"
                  justifyContent="center"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    paddingBottom="12px"
                  >
                    <OcFormSwitch
                      id="automaticPasswordGeneration"
                      checked={automaticPasswordGeneration}
                      inputProps={{ "aria-label": "controlled" }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAutomaticPasswordGeneration(e.target.checked)
                      }
                      disabled={false}
                    />
                    <Box width="16px" />
                    <Typography variant="body1">
                      {t("MY_HR_AUTOMATIC_PASSWORD_GENERATION")}
                    </Typography>
                  </Box>
                  <Typography variant="caption">
                    {t("MY_HR_AUTOMATIC_PASSWORD_GENERATION_INFO")}
                  </Typography>
                </Box>
              </>
            )}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              padding="38px 0px"
              style={{
                position: "relative",
              }}
            >
              <Button
                id="import-buttom"
                variant="contained"
                color="primary"
                disableElevation
                onClick={handleShowImportDialog}
                disabled={importLocked}
              >
                {t("MY_HR_IMPORT_USERS")}
              </Button>
            </Box>
            {importLocked ? (
              <Box display="flex" justifyContent="space-around">
                <Typography variant="caption">
                  {t("MY_HR_IMPORT_SSHR_USERS_LOCKED_INFO")}
                </Typography>
              </Box>
            ) : (
              <Box display="flex" justifyContent="space-around">
                <Typography variant="caption">
                  {t("MY_HR_IMPORT_USERS_TEXT_INFO")}
                </Typography>
              </Box>
            )}
            <Box height="32px" />
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};

export default MyHrUsersImportJDolBerCard;
